/**
 * Phase 13 — Reports / KPI Dashboard helpers.
 *
 * Tiny axios wrappers + formatters used across all report tabs and the
 * summary strip. All endpoints are owner-only (server enforces 403).
 */
import api from "./api";

export async function fetchSummary(params) {
  const { data } = await api.get("/reports/summary", { params });
  return data;
}

export async function fetchRevenue(params) {
  const { data } = await api.get("/reports/revenue", { params });
  return data;
}

export async function fetchOperations(params) {
  const { data } = await api.get("/reports/operations", { params });
  return data;
}

export async function fetchFleet(params) {
  const { data } = await api.get("/reports/fleet", { params });
  return data;
}

export async function fetchTeam(params) {
  const { data } = await api.get("/reports/team", { params });
  return data;
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

export function formatCents(cents) {
  if (cents === null || cents === undefined) return "—";
  const dollars = cents / 100;
  // No fractional pennies on big stat tiles.
  if (Math.abs(dollars) >= 1000) {
    return `$${dollars.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  }
  return `$${dollars.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatMinutes(mins) {
  if (mins === null || mins === undefined) return "—";
  if (mins < 60) return `${Math.round(mins)}m`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

export function formatPercent(rate) {
  if (rate === null || rate === undefined) return "—";
  return `${(rate * 100).toFixed(1)}%`;
}

export function formatDays(days) {
  if (days === null || days === undefined) return "—";
  if (days < 1) return `${(days * 24).toFixed(1)}h`;
  return `${days.toFixed(1)}d`;
}

// ---------------------------------------------------------------------------
// Date range presets
// ---------------------------------------------------------------------------

const fmtDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export function presetRange(preset) {
  const today = new Date();
  const to = fmtDate(today);
  switch (preset) {
    case "today":
      return { from: to, to };
    case "this_week": {
      const d = new Date(today);
      const day = (d.getDay() + 6) % 7; // Monday-start week
      d.setDate(d.getDate() - day);
      return { from: fmtDate(d), to };
    }
    case "this_month": {
      const d = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: fmtDate(d), to };
    }
    case "last_30":
    default: {
      const d = new Date(today);
      d.setDate(d.getDate() - 29);
      return { from: fmtDate(d), to };
    }
    case "last_90": {
      const d = new Date(today);
      d.setDate(d.getDate() - 89);
      return { from: fmtDate(d), to };
    }
  }
}

export const PRESETS = [
  { key: "today", label: "Today" },
  { key: "this_week", label: "This week" },
  { key: "this_month", label: "This month" },
  { key: "last_30", label: "Last 30 days" },
  { key: "last_90", label: "Last 90 days" },
];

// Severity → tailwind color class used across charts and pills.
export const SEVERITY_COLORS = {
  critical: "#dc2626",
  high: "#f97316",
  medium: "#f59e0b",
  low: "#3b82f6",
  info: "#64748b",
};

// Phosphor / brand-tone palette for non-severity bar charts.
export const CHART_PALETTE = [
  "#f59e0b", // amber
  "#0ea5e9", // sky
  "#10b981", // emerald
  "#a855f7", // purple
  "#ef4444", // red
  "#6366f1", // indigo
  "#14b8a6", // teal
];
